import styled from '@emotion/styled'
import {css} from '@emotion/react'

import {theme} from '../../theme'

// ─────────────────────────────────────────────
// Size styles
// ─────────────────────────────────────────────

const sizeStyles = ({size = 'md'}) => {
  switch (size) {
    case 'sm':
      return css`
        height: 32px;
        padding: 0 ${theme.space[2]};
        font-size: ${theme.fontSizes.sm};
      `

    case 'lg':
      return css`
        height: 46px;
        padding: 0 ${theme.space[4]};
        font-size: ${theme.fontSizes.lg};
      `

    case 'md':
    default:
      return css`
        height: 38px;
        padding: 0 ${theme.space[3]};
        font-size: ${theme.fontSizes.base};
      `
  }
}

// ─────────────────────────────────────────────
// Variant styles
// ─────────────────────────────────────────────

const variantStyles = ({variant = 'default', hasError}) => {
  switch (variant) {
    case 'form':
      return css`
        color: #6a7181;

        background: #ffffff;
        // background: #f7f7f7;
        border: 1px solid ${hasError ? '#d92d20' : '#E5E7EB'};
        border-radius: 10px;

        &:hover:not(:disabled) {
          border-color: #c8ccd2;
        }

        &:focus {
          border-color: ${hasError ? '#d92d20' : '#7c5cff'};
          box-shadow: 0 0 0 3px
            ${hasError ? 'rgba(217, 45, 32, 0.12)' : 'rgba(124, 92, 255, 0.15)'};
        }
      `

    case 'minimal':
      return css`
        background: transparent;
        border: none;
        border-bottom: 1px solid
          ${hasError ? '#d92d20' : theme.colors.border.primary};
        border-radius: 0;
      `

    case 'default':
    default:
      return css`
        background: ${theme.colors.input.bg};
        border: 1px solid
          ${hasError
            ? theme.colors.input.borderError
            : theme.colors.input.border};
        border-radius: ${theme.radii.xs};

        &:hover:not(:disabled) {
          border-color: ${theme.colors.input.borderHover};
        }

        &:focus {
          border-color: ${hasError
            ? theme.colors.input.borderError
            : theme.colors.input.borderFocus};

          box-shadow: 0 0 0 3px
            ${hasError
              ? theme.colors.input.errorRing
              : theme.colors.input.focusRing};
        }
      `
  }
}

// ─────────────────────────────────────────────
// Styled Components
// ─────────────────────────────────────────────

export const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.space[1]};
  width: 100%;
`

export const LabelRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`

export const LabelIcon = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;

  color: #6a7181;

  svg {
    width: 12px;
    height: 12px;
  }
`

export const LabelEl = styled.label`
  display: block;

  font-family: ${({variant}) =>
    variant === 'payment' ? "'Open Sans', Regular" : 'inherit'};

  font-size: ${({variant}) =>
    variant === 'form' || variant === 'payment' ? '12px' : theme.fontSizes.md};

  font-weight: ${({variant}) =>
    variant === 'payment' ? '700' : theme.fontWeights.medium};

  line-height: ${({variant}) => (variant === 'payment' ? '100%' : 'normal')};

  letter-spacing: ${({variant}) => (variant === 'payment' ? '0' : 'normal')};

  color: ${({variant}) =>
    variant === 'payment' ? '#000000' : theme.colors.text.primary};

  margin: ${({variant}) => (variant === 'payment' ? '0' : undefined)};
`

export const LabelReq = styled.span`
  display: block;

  font-size: ${({variant}) =>
    variant === 'form' ? '12px' : theme.fontSizes.md};

  font-weight: ${({variant}) =>
    variant === 'form' ? theme.fontWeights.medium : theme.fontWeights.medium};

  color: ${theme.colors.status.danger.text};
`

export const SelectWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`

export const StyledSelect = styled.select`
  width: 100%;

  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;

  color: ${theme.colors.input.text};

  border: 1px solid
    ${({hasError}) =>
      hasError ? theme.colors.input.borderError : theme.colors.input.border};

  outline: none;

  transition: border-color ${theme.transitions.normal},
    box-shadow ${theme.transitions.normal};

  cursor: pointer;

  padding-left: ${({hasLeftIcon}) =>
    hasLeftIcon ? theme.space[8] : undefined};

  padding-right: ${theme.space[8]};

  ${sizeStyles}

  ${variantStyles}


  &:hover:not(:disabled) {
    border-color: ${theme.colors.input.borderHover};
  }

  &:focus {
    border-color: ${({hasError}) =>
      hasError
        ? theme.colors.input.borderError
        : theme.colors.input.borderFocus};

    box-shadow: 0 0 0 3px
      ${({hasError}) =>
        hasError ? theme.colors.input.errorRing : theme.colors.input.focusRing};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    background: ${theme.colors.bg.disabled};
  }
`

export const IconSlot = styled.span`
  position: absolute;

  top: 50%;

  transform: translateY(-50%);

  display: flex;
  align-items: center;
  justify-content: center;

  // color: ${theme.colors.text.primary};
  // color: #667085;

  pointer-events: none;

  font-size: 18px;

  ${({side}) =>
    side === 'left'
      ? css`
          left: ${theme.space[3]};
        `
      : css`
          right: ${theme.space[3]};
        `}
`

export const HelperText = styled.p`
  font-size: ${theme.fontSizes.sm};

  margin: 0;

  color: ${({isError}) =>
    isError ? theme.colors.status.danger.text : theme.colors.text.muted};
`
